var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
/**
 * UI component factories — controls.
 */
import { registerComponent } from '../registryCore';
import { bool, clamp, getParts, getState, num, setParts, setState, str, syntheticEvent, } from '../helpers';
import { wireButtonStates, wirePointerDrag, wireToggle, } from '../interaction';
import { hasCustomFontSize, hasCustomTextColor, resolveNodeTypography, } from '../nodeTheme';
import { UI, createGroup, canvasSurface } from './shared';
/** Button — hover, active, disabled states */
registerComponent('button', function (props, app) {
    var width = num(props, 'width', 128);
    var size = str(props, 'size', 'md');
    var height = size === 'sm' ? 32 : size === 'lg' ? 44 : num(props, 'height', UI().controlHeight);
    var customFont = hasCustomFontSize(props);
    var customText = hasCustomTextColor(props);
    var typo = resolveNodeTypography(app, props, {
        text: UI().textInverse,
        textMuted: UI().textMuted,
        fontSize: UI().fontSize,
        fontSizeSm: UI().fontSizeSm,
        fontSizeLg: UI().fontSizeLg,
    });
    var fontSize = customFont
        ? typo.fontSize
        : size === 'sm'
            ? UI().fontSizeSm
            : size === 'lg'
                ? UI().fontSizeLg
                : UI().fontSize;
    var label = str(props, 'label', 'Button');
    var disabled = bool(props, 'disabled', false);
    var variant = str(props, 'variant', 'primary');
    var hasCustomFill = Boolean(str(props, 'fill', ''));
    var fill = str(props, 'fill', '') ||
        (variant === 'secondary' ? UI().secondary : variant === 'ghost' ? UI().surface : variant === 'danger' ? UI().danger : UI().primary);
    var group = createGroup(app, 'button', props, {
        focusable: !disabled,
        role: 'button',
        metadata: {
            componentType: 'button',
            label: label,
            componentState: {
                label: label,
                width: width,
                height: height,
                disabled: disabled,
                fill: fill,
                variant: variant,
                size: size,
                hasCustomFill: hasCustomFill,
                hasCustomFontSize: customFont,
                hasCustomColor: customText,
                fontSize: customFont ? fontSize : undefined,
                textColor: customText ? typo.text : undefined,
                color: props.color,
                uiTheme: props.uiTheme,
            },
        },
    });
    setState(group, {
        label: label,
        width: width,
        height: height,
        disabled: disabled,
        fill: fill,
        variant: variant,
        size: size,
        hasCustomFill: hasCustomFill,
        hasCustomFontSize: customFont,
        hasCustomColor: customText,
        fontSize: customFont ? fontSize : undefined,
        textColor: customText ? typo.text : undefined,
        color: props.color,
        uiTheme: props.uiTheme,
    });
    var textColor = customText
        ? typo.text
        : variant === 'ghost'
            ? UI().textSecondary
            : UI().textInverse;
    var bg = app.roundedRect({
        width: width,
        height: height,
        cornerRadius: UI().radius,
        fill: disabled ? UI().borderStrong : fill,
        stroke: variant === 'ghost' ? UI().border : null,
        strokeWidth: variant === 'ghost' ? 1 : 0,
        shadow: disabled ? null : variant === 'primary' || variant === 'danger' ? UI().shadowPrimary : UI().shadowSm,
    });
    var text = app.text({
        text: label,
        fontSize: fontSize,
        fontWeight: '600',
        fill: disabled ? UI().textMuted : textColor,
        // Canvas textAlign centers on x — must be mid-button, not the left edge.
        x: width / 2,
        y: (height - fontSize) / 2,
        textAlign: 'center',
    });
    group.add(bg, text);
    setParts(group, { bg: bg, text: text });
    wireButtonStates(group, function (_a) {
        var _b, _c, _d, _e;
        var hover = _a.hover, active = _a.active, dis = _a.disabled;
        var parts = getParts(group);
        var ui = UI();
        var st = getState(group);
        var v = String((_b = st.variant) !== null && _b !== void 0 ? _b : variant);
        if (dis) {
            parts.bg.fill = ui.borderStrong;
            return (_c = group.getApp()) === null || _c === void 0 ? void 0 : _c.requestRender();
        }
        var base = st.hasCustomFill
            ? String((_d = st.fill) !== null && _d !== void 0 ? _d : fill)
            : v === 'secondary'
                ? ui.secondary
                : v === 'ghost'
                    ? ui.surface
                    : v === 'danger'
                        ? ui.danger
                        : ui.primary;
        var hoverColor = v === 'secondary'
            ? ui.secondaryHover
            : v === 'ghost'
                ? ui.surfaceInset
                : v === 'danger'
                    ? '#b91c1c'
                    : ui.primaryHover;
        var activeColor = v === 'secondary'
            ? ui.textSecondary
            : v === 'ghost'
                ? ui.surfaceMuted
                : v === 'danger'
                    ? '#991b1b'
                    : ui.primaryActive;
        parts.bg.fill = active ? activeColor : hover ? hoverColor : base;
        (_e = group.getApp()) === null || _e === void 0 ? void 0 : _e.requestRender();
    });
    return group;
});
registerComponent('label', function (props, app) {
    var hasCustomColor = hasCustomTextColor(props);
    var hasCustomFs = hasCustomFontSize(props);
    var typo = resolveNodeTypography(app, props, {
        text: UI().textMuted,
        textMuted: UI().textMuted,
        fontSize: UI().fontSizeSm,
        fontSizeSm: UI().fontSizeSm,
        fontSizeLg: UI().fontSizeLg,
    });
    var fontSize = hasCustomFs ? typo.fontSize : typo.fontSizeSm;
    var node = app.text(__assign({ text: str(props, 'text', ''), fontSize: fontSize, fontWeight: str(props, 'fontWeight', '600'), fill: hasCustomColor ? typo.text : UI().textMuted }, props));
    // Re-apply after spread so props.fontSize/color do not fight resolved values incorrectly
    if (!hasCustomFs)
        node.fontSize = typo.fontSizeSm;
    else
        node.fontSize = typo.fontSize;
    if (!hasCustomColor)
        node.fill = UI().textMuted;
    else
        node.fill = typo.text;
    node.metadata.componentType = 'label';
    setState(node, {
        text: str(props, 'text', ''),
        fontSize: fontSize,
        color: props.color,
        textColor: props.textColor,
        uiTheme: props.uiTheme,
        demoId: props.demoId,
        hasCustomColor: hasCustomColor,
        hasCustomFontSize: hasCustomFs,
    });
    if (props.demoId != null)
        node.metadata.demoId = props.demoId;
    return node;
});
registerComponent('card', function (props, app) {
    var width = num(props, 'width', 280);
    var height = num(props, 'height', 160);
    var title = props.title;
    var subtitle = str(props, 'subtitle', '');
    var elevated = bool(props, 'elevated', false);
    var group = createGroup(app, 'card', props);
    var bg = canvasSurface(app, width, height, { radius: UI().radiusLg, elevated: elevated });
    group.add(bg);
    var headerH = title || subtitle ? 40 : 0;
    var header;
    var titleNode;
    var subtitleNode;
    if (headerH > 0) {
        header = app.roundedRect({
            width: width,
            height: headerH,
            cornerRadius: UI().radiusLg,
            fill: UI().surfaceMuted,
            stroke: UI().border,
            strokeWidth: 1,
            listening: false,
        });
        group.add(header);
        if (title) {
            titleNode = app.text({
                text: String(title).toUpperCase(),
                fontSize: UI().fontSizeSm,
                fontWeight: '700',
                fill: UI().textMuted,
                x: 16,
                y: 12,
                listening: false,
            });
            group.add(titleNode);
        }
        if (subtitle) {
            subtitleNode = app.text({
                text: subtitle,
                fontSize: UI().fontSize,
                fontWeight: '500',
                fill: UI().textSecondary,
                x: 16,
                y: title ? 26 : 12,
                listening: false,
            });
            group.add(subtitleNode);
        }
    }
    setParts(group, __assign(__assign(__assign({ bg: bg }, (header ? { header: header } : {})), (titleNode ? { title: titleNode } : {})), (subtitleNode ? { subtitle: subtitleNode } : {})));
    setState(group, { width: width, height: height, title: props.title, subtitle: subtitle, actions: props.actions, elevated: props.elevated });
    return group;
});
registerComponent('progressBar', function (props, app) {
    var width = num(props, 'width', 200);
    var size = str(props, 'size', 'md');
    var height = size === 'lg' ? 12 : size === 'sm' ? 6 : num(props, 'height', 8);
    var value = clamp(num(props, 'value', 0), 0, 100);
    var variant = str(props, 'variant', 'default');
    var fillColor = variant === 'success'
        ? UI().success
        : variant === 'warning'
            ? UI().warning
            : variant === 'danger'
                ? UI().danger
                : str(props, 'fill', UI().primary);
    var group = createGroup(app, 'progressBar', props, {
        role: 'progressbar',
        ariaValueNow: value,
        ariaValueMin: 0,
        ariaValueMax: 100,
    });
    var track = app.roundedRect({ width: width, height: height, cornerRadius: height / 2, fill: UI().surfaceInset, listening: false });
    var fillBar = app.roundedRect({
        width: (width * value) / 100,
        height: height,
        cornerRadius: height / 2,
        fill: fillColor,
        listening: false,
    });
    group.add(track, fillBar);
    if (props.label) {
        group.add(app.text({
            text: props.label,
            fontSize: UI().fontSizeSm,
            fontWeight: '600',
            fill: UI().textSecondary,
            x: 0,
            y: -18,
            listening: false,
        }));
    }
    setParts(group, { track: track, fillBar: fillBar });
    setState(group, { width: width, height: height, value: value, label: props.label, variant: variant, size: size, disabled: props.disabled });
    return group;
});
/** Slider — drag thumb, emit change */
registerComponent('slider', function (props, app) {
    var _a;
    var width = num(props, 'width', 200);
    var min = num(props, 'min', 0);
    var max = num(props, 'max', 100);
    var value = clamp(num(props, 'value', 50), min, max);
    var group = createGroup(app, 'slider', props, {
        focusable: true,
        role: 'slider',
        ariaValueNow: value,
        ariaValueMin: min,
        ariaValueMax: max,
        metadata: { componentType: 'slider', label: (_a = props.label) !== null && _a !== void 0 ? _a : 'Slider' },
    });
    var track = app.roundedRect({ width: width, height: 6, y: 12, cornerRadius: 3, fill: UI().surfaceInset, listening: false });
    var fill = app.roundedRect({ width: 0, height: 6, y: 12, cornerRadius: 3, fill: UI().primary, listening: false });
    var thumb = app.circle({
        x: 0,
        y: 12,
        radius: 10,
        fill: UI().surface,
        stroke: UI().primary,
        strokeWidth: 2,
        shadow: UI().shadowMd,
        listening: false,
    });
    group.add(track, fill, thumb);
    setParts(group, { track: track, fill: fill, thumb: thumb });
    var updateVisual = function (v) {
        var _a;
        var pct = (v - min) / (max - min);
        fill.width = width * pct;
        thumb.x = width * pct - 10;
        group.ariaValueNow = v;
        (_a = group.getApp()) === null || _a === void 0 ? void 0 : _a.requestRender();
    };
    updateVisual(value);
    setState(group, { width: width, min: min, max: max, value: value, label: props.label, disabled: props.disabled, size: props.size });
    var setValue = function (worldX) {
        var localX = clamp(worldX - group.x, 0, width);
        var pct = localX / width;
        value = min + pct * (max - min);
        setState(group, { value: value });
        updateVisual(value);
    };
    wirePointerDrag(group, function (wx) { return setValue(wx); }, function () {
        group.emit('change', syntheticEvent('change', group, { value: getState(group).value }));
    });
    return group;
});
/** Checkbox — toggle, aria-checked */
registerComponent('checkbox', function (props, app) {
    var _a;
    var checked = bool(props, 'checked', false);
    var disabled = bool(props, 'disabled', false);
    var group = createGroup(app, 'checkbox', props, {
        focusable: !disabled,
        role: 'checkbox',
        ariaChecked: checked,
        metadata: { componentType: 'checkbox', label: (_a = props.label) !== null && _a !== void 0 ? _a : 'Checkbox' },
    });
    var box = app.roundedRect({
        width: 20,
        height: 20,
        cornerRadius: 5,
        fill: disabled ? UI().surfaceMuted : checked ? UI().primary : UI().surface,
        stroke: disabled ? UI().border : checked ? UI().primary : UI().borderStrong,
        strokeWidth: 1.5,
        shadow: checked && !disabled ? UI().shadowSm : null,
        listening: false,
    });
    var mark = app.text({
        text: '✓',
        x: 4,
        y: 1,
        fontSize: 14,
        fontWeight: 'bold',
        fill: UI().textInverse,
        visible: checked,
        listening: false,
    });
    group.add(box, mark);
    if (props.label) {
        group.add(app.text({
            text: props.label,
            x: 30,
            y: 2,
            fontSize: UI().fontSize,
            fill: UI().textSecondary,
            listening: false,
        }));
    }
    setParts(group, { box: box, mark: mark });
    setState(group, { checked: checked, label: props.label, disabled: disabled, size: props.size });
    if (disabled) {
        group.opacity = 0.55;
        group.listening = false;
        return group;
    }
    wireToggle(group, 'checked', function (v) {
        box.fill = v ? UI().primary : UI().surface;
        box.stroke = v ? UI().primary : UI().borderStrong;
        mark.visible = v;
        group.ariaChecked = v;
    });
    return group;
});
/** Toggle switch */
registerComponent('toggle', function (props, app) {
    var _a;
    var on = bool(props, 'value', false);
    var disabled = bool(props, 'disabled', false);
    var group = createGroup(app, 'toggle', props, {
        focusable: !disabled,
        role: 'switch',
        ariaChecked: on,
        metadata: { componentType: 'toggle', label: (_a = props.label) !== null && _a !== void 0 ? _a : 'Toggle' },
    });
    var track = app.roundedRect({
        width: 48,
        height: 26,
        cornerRadius: 13,
        fill: disabled ? UI().border : on ? UI().primary : UI().borderStrong,
        listening: false,
    });
    var knob = app.circle({
        x: on ? 24 : 2,
        y: 3,
        radius: 10,
        fill: UI().surface,
        shadow: UI().shadowMd,
        listening: false,
    });
    group.add(track, knob);
    setParts(group, { track: track, knob: knob });
    setState(group, { value: on, label: props.label, disabled: disabled, size: props.size });
    if (disabled) {
        group.opacity = 0.55;
        group.listening = false;
        return group;
    }
    wireToggle(group, 'value', function (v) {
        track.fill = v ? UI().primary : UI().borderStrong;
        knob.x = v ? 24 : 2;
        group.ariaChecked = v;
    });
    return group;
});
/** Input — text field (native in HTML renderer) */
registerComponent('input', function (props, app) {
    var _a;
    var width = num(props, 'width', 240);
    var height = num(props, 'height', UI().inputHeight);
    var value = str(props, 'value', '');
    var placeholder = str(props, 'placeholder', '');
    var disabled = bool(props, 'disabled', false);
    var invalid = bool(props, 'invalid', false);
    var customFont = hasCustomFontSize(props);
    var customText = hasCustomTextColor(props);
    var typo = resolveNodeTypography(app, props, {
        text: UI().text,
        textMuted: UI().textPlaceholder,
        fontSize: UI().fontSize,
        fontSizeSm: UI().fontSizeSm,
        fontSizeLg: UI().fontSizeLg,
    });
    var fontSize = customFont ? typo.fontSize : UI().fontSize;
    var group = createGroup(app, 'input', props, {
        focusable: !disabled,
        role: 'textbox',
        metadata: { componentType: 'input', label: (_a = props.label) !== null && _a !== void 0 ? _a : (placeholder || 'Input') },
    });
    var bg = app.roundedRect({
        width: width,
        height: height,
        cornerRadius: UI().radius,
        fill: disabled ? UI().surfaceMuted : UI().surface,
        stroke: invalid ? UI().danger : UI().border,
        strokeWidth: invalid ? 2 : 1,
        shadow: disabled ? null : UI().shadowSm,
        listening: false,
    });
    var text = app.text({
        text: value || placeholder,
        fontSize: fontSize,
        fill: value
            ? customText
                ? typo.text
                : UI().text
            : UI().textPlaceholder,
        x: 12,
        y: (height - fontSize) / 2,
        listening: false,
    });
    group.add(bg, text);
    setParts(group, { bg: bg, text: text });
    setState(group, {
        width: width,
        height: height,
        value: value,
        placeholder: placeholder,
        label: props.label,
        disabled: disabled,
        invalid: invalid,
        error: props.error,
        hasCustomFontSize: customFont,
        hasCustomColor: customText,
        fontSize: customFont ? fontSize : undefined,
        textColor: customText ? typo.text : undefined,
        color: props.color,
        uiTheme: props.uiTheme,
    });
    if (disabled)
        group.opacity = 0.65;
    return group;
});
/** TextArea — multiline input */
registerComponent('textarea', function (props, app) {
    var width = num(props, 'width', 280);
    var height = num(props, 'height', 96);
    var value = str(props, 'value', '');
    var disabled = bool(props, 'disabled', false);
    var invalid = bool(props, 'invalid', false);
    var group = createGroup(app, 'textarea', props, {
        focusable: !disabled,
        role: 'textbox',
        metadata: { componentType: 'textarea', multiline: true },
    });
    var bg = app.roundedRect({
        width: width,
        height: height,
        cornerRadius: UI().radius,
        fill: disabled ? UI().surfaceMuted : UI().surface,
        stroke: invalid ? UI().danger : UI().border,
        strokeWidth: invalid ? 2 : 1,
        shadow: disabled ? null : UI().shadowSm,
        listening: false,
    });
    var text = app.text({
        text: value || str(props, 'placeholder', ''),
        fontSize: UI().fontSize,
        fill: value ? UI().text : UI().textPlaceholder,
        x: 12,
        y: 12,
        listening: false,
    });
    group.add(bg, text);
    setParts(group, { bg: bg, text: text });
    setState(group, { width: width, height: height, value: value, rows: num(props, 'rows', 4), label: props.label, placeholder: props.placeholder, disabled: disabled, invalid: invalid, error: props.error });
    if (disabled)
        group.opacity = 0.65;
    return group;
});
/** Radio — group selection via metadata.group */
registerComponent('radio', function (props, app) {
    var selected = bool(props, 'selected', false);
    var disabled = bool(props, 'disabled', false);
    var groupName = str(props, 'group', 'default');
    var group = createGroup(app, 'radio', props, {
        focusable: !disabled,
        role: 'radio',
        ariaChecked: selected,
        metadata: { componentType: 'radio', group: groupName, label: props.label },
    });
    // Circle nodes draw with center at (radius, radius) in local space.
    var outer = app.circle({
        x: 0,
        y: 0,
        radius: 10,
        fill: disabled ? UI().surfaceMuted : UI().surface,
        stroke: disabled ? UI().border : selected ? UI().primary : UI().borderStrong,
        strokeWidth: selected ? 2 : 1.5,
        listening: false,
    });
    var inner = app.circle({
        x: 5,
        y: 5,
        radius: 5,
        fill: selected ? UI().primary : 'transparent',
        listening: false,
    });
    group.add(outer, inner);
    if (props.label) {
        group.add(app.text({
            text: props.label,
            x: 28,
            y: 3,
            fontSize: UI().fontSize,
            fill: UI().textSecondary,
            listening: false,
        }));
    }
    setParts(group, { outer: outer, inner: inner });
    setState(group, { selected: selected, group: groupName, label: props.label, disabled: disabled, size: props.size });
    if (disabled) {
        group.opacity = 0.55;
        group.listening = false;
        return group;
    }
    group.on('click', function () {
        var _a;
        setState(group, { selected: true });
        group.ariaChecked = true;
        inner.fill = UI().primary;
        outer.stroke = UI().primary;
        group.emit('change', syntheticEvent('change', group, { value: groupName, payload: groupName }));
        (_a = group.getApp()) === null || _a === void 0 ? void 0 : _a.requestRender();
    });
    return group;
});
